import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { useEffect, useRef } from "react";
import { HelmetProvider } from 'react-helmet-async';
import Index from "./pages/Index";
import ToolDetails from "./pages/ToolDetails";
import Auth from "./pages/Auth";
import SubmitApp from "./pages/SubmitApp";
import MyListings from "./pages/MyListings";
import EditApp from "./pages/EditApp";
import CategoryPage from "./pages/CategoryPage";
import CategoriesIndex from "./pages/CategoriesIndex";
import SportPage from "./pages/SportPage";
import SportsIndex from "./pages/SportsIndex";
import PlatformPage from "./pages/PlatformPage";
import PlatformsIndex from "./pages/PlatformsIndex";
import AllTools from "./pages/AllTools";
import NotFound from "./pages/NotFound";
import AuthHandler from "./components/AuthHandler";
import useAnalytics from './hooks/useAnalytics';
import GTMProvider from './components/GTMProvider';
import PricingPage from './pages/Pricing';

const queryClient = new QueryClient();
const helmetContext = {};

// Enhanced component to track page views on every route change
// This should not interfere with the MetaTags component
const PageTracker = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { trackPageView, trackEvent } = useAnalytics();
  const prevPathRef = useRef<string>("");
  
  // Track page view when location changes
  useEffect(() => {
    // Get current path and decode it for proper tracking
    const currentPath = decodeURIComponent(location.pathname);
    
    // Don't update meta tags here - just get the current title
    // This prevents conflicts with MetaTags component
    const currentTitle = document.title || 'FitnessAppSearch';
    
    // Only track if this is a new path different from the previous one
    if (currentPath !== prevPathRef.current) {
      console.log(`Tracking page view: ${currentPath} - ${currentTitle}`);
      
      // Map route to page name for better analytics reporting
      let pageName = currentTitle;
      if (currentPath === '/') pageName = 'Home';
      if (currentPath === '/all-tools') pageName = 'All Tools';
      if (currentPath === '/my-listings') pageName = 'My Listings';
      if (currentPath === '/submit-app') pageName = 'Submit App';
      if (currentPath === '/auth') pageName = 'Authentication';
      if (currentPath === '/categories') pageName = 'Categories';
      if (currentPath === '/sports') pageName = 'Sports';
      if (currentPath === '/platforms') pageName = 'Platforms';
      if (currentPath.startsWith('/tool/')) pageName = 'Tool Details';
      if (currentPath.startsWith('/category/')) pageName = 'Category';
      if (currentPath.startsWith('/sport/')) pageName = 'Sport';
      if (currentPath.startsWith('/platform/')) pageName = 'Platform';
      
      // Track the page view without modifying meta tags
      trackPageView(currentPath, pageName);
      
      // Also track as a separate event for more detailed analytics
      trackEvent('page_view', { 
        path: currentPath,
        page_name: pageName,
        referrer: document.referrer || 'direct'
      });
      
      // Update the previous path
      prevPathRef.current = currentPath;
    }
  }, [location.pathname, location.search, trackPageView, trackEvent]);
  
  return null;
};

const App = () => {
  return (
    <HelmetProvider context={helmetContext}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <GTMProvider>
              <AuthHandler>
                <PageTracker />
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/tool/:slug" element={<ToolDetails />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/submit-app" element={<SubmitApp />} />
                  <Route path="/my-listings" element={<MyListings />} />
                  <Route path="/edit-app/:id" element={<EditApp />} />
                  <Route path="/all-tools" element={<AllTools />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  
                  {/* Category pages */}
                  <Route path="/categories" element={<CategoriesIndex />} />
                  <Route path="/category/:slug" element={<CategoryPage />} />
                  
                  {/* Sport pages */}
                  <Route path="/sports" element={<SportsIndex />} />
                  <Route path="/sport/:slug" element={<SportPage />} />
                  
                  {/* Platform pages */}
                  <Route path="/platforms" element={<PlatformsIndex />} />
                  <Route path="/platform/:slug" element={<PlatformPage />} />
                  
                  {/* 404 page for all other routes */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </AuthHandler>
            </GTMProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
